import axios from "axios";
import { Response } from "../../../../../shared/models/Response";
import { Http } from "@status/codes";
import { ChatResponse } from "../Model/ChatResponse.model";
import { InputChat } from "../Model/InputChat.model";
import { HistoryChat } from "../Model/HistoryChat.model";

type StreamPayload = ChatResponse & {
  content?: string;
  delta?: string;
  text?: string;
  finalResponseToUser?: ChatResponse["finalResponseToUser"] & {
    calledTool?: Array<{ name: string; args?: any }>;
  };
};

const resultOf = (payload: StreamPayload): any =>
  (payload as any).data || payload;

const readableText = (text = ""): string => {
  try {
    const parsed = JSON.parse(text);
    return parsed.text || text;
  } catch {
    return text;
  }
};

const navigationResponseOf = (payload: StreamPayload): any => {
  const result = resultOf(payload);
  const finalResponse = result.finalResponseToUser;
  const navigationTool = finalResponse?.calledTool
    ?.slice()
    .reverse()
    .find((tool: any) => tool.name === "uiNavigation");

  if (!navigationTool) return finalResponse;

  console.log("navigationTool", navigationTool);
  return {
    ...finalResponse,
    text: navigationTool.args?.text || readableText(finalResponse.text),
    targetView: navigationTool.args?.targetView,
    paramsView: navigationTool.args?.paramsView
  };
};

export class ChatService {
  private baseUrl: string;
  constructor() {
    this.baseUrl = `${process.env.VUE_APP_CHAT_API}`;
  }

  private async streamRequest(
    url: string,
    body: object,
    onChunk: (chunk: string) => void,
    fallbackSessionId = ""
  ): Promise<Response<ChatResponse>> {
    const response: Response<ChatResponse> = {
      code: 0,
      message: ""
    };
    try {
      const responseChat = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("TOKEN") || "",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...body, stream: true })
      });

      if (!responseChat.ok || !responseChat.body) {
        response.code = responseChat.status || Http.Forbidden;
        return response;
      }

      const reader = responseChat.body.getReader();
      const decoder = new TextDecoder();
      const isServerSentEvents = responseChat.headers
        .get("content-type")
        ?.includes("text/event-stream");
      let buffer = "";
      let streamDone = false;
      let responseData: ChatResponse | undefined;
      let structuredTextBuffer = "";

      const processChunk = (chunk: string) => {
        if (/^event:\s*/i.test(chunk)) return;

        const value = chunk.replace(/^data:\s?/, "").trim();
        if (!value || value === "[DONE]") return;

        try {
          const parsed = JSON.parse(value) as StreamPayload;
          const result = resultOf(parsed);
          const finalResponseToUser = navigationResponseOf(parsed);
          if (finalResponseToUser && (parsed as any).data) {
            (parsed as any).data.finalResponseToUser = finalResponseToUser;
          }
          responseData = {
            ...responseData,
            ...parsed,
            ...(result && typeof result === "object" ? result : {}),
            ...(finalResponseToUser ? { finalResponseToUser } : {}),
            sessionId:
              parsed.sessionId ||
              result?.sessionId ||
              responseData?.sessionId ||
              fallbackSessionId
          };
          const text =
            parsed.content ||
            parsed.delta ||
            parsed.text ||
            readableText(result.finalResponseToUser?.text) ||
            "";
          // The done event contains the complete accumulated response. The
          // text chunks have already been rendered incrementally.
          if (text && !(parsed as any).data) {
            const candidate = `${structuredTextBuffer}${text}`;
            if (structuredTextBuffer || candidate.trimStart().startsWith("{")) {
              structuredTextBuffer = candidate;
              try {
                const structuredResponse = JSON.parse(structuredTextBuffer);
                structuredTextBuffer = "";
                if (structuredResponse.text) onChunk(structuredResponse.text);
              } catch {
                // Wait for the remaining streamed JSON fragments.
              }
            } else {
              onChunk(text);
            }
          }
        } catch {
          onChunk(value);
        }
      };

      while (!streamDone) {
        const { done, value } = await reader.read();
        streamDone = done;
        const decoded = decoder.decode(value, { stream: !done });
        if (!isServerSentEvents) {
          if (decoded) processChunk(decoded);
          if (done) break;
          continue;
        }

        buffer += decoded;
        const chunks = buffer.split(/\r?\n/);
        buffer = chunks.pop() || "";
        chunks.forEach(processChunk);
        if (done) break;
      }
      if (buffer.trim()) processChunk(buffer);

      return { code: Http.Ok, message: "", data: responseData };
    } catch (error) {
      response.code = Http.Forbidden;
      return response;
    }
  }
  async sendChatProp(
    input: InputChat,
    onChunk: (chunk: string) => void
  ): Promise<Response<ChatResponse>> {
    const response: Response<ChatResponse> = {
      code: 0,
      message: ""
    };
    const userInput = input.message || "";
    const hasAttachments = Boolean(
      input.attachments && input.attachments.length > 0
    );
    if (userInput.trim().length === 0 && !hasAttachments) {
      response.code = Http.BadRequest;
      response.message = "";
      return response;
    }

    const payload = {
      ...input,
      attachments: input.attachments?.map(att => ({
        name:
          att.name ||
          (att as any).fileName ||
          (att as any).filename ||
          (att as any).file_name ||
          (att as any).originalName ||
          (att as any).originalname ||
          (att as any).tempName ||
          (att as any).tempFileName ||
          "Archivo adjunto",
        mimeType: att.mimeType,
        data: att.data,
        fileUri: att.fileUri
      }))
    };

    const isChatAssistant = input.viewName === "ChatAssistant";
    const isPatientProfile = input.viewName === "Profile";
    const isDashboard =
      input.viewName === "Dashboard" ||
      input.context?.activeViewContext?.view?.name === "Dashboard";
    const userTarget =
      isChatAssistant || isPatientProfile
        ? "patient"
        : isDashboard
        ? "dashboard"
        : "user";

    return this.streamRequest(
      `${this.baseUrl}/ai-agent/chat/${userTarget}`,
      payload,
      onChunk,
      input.sessionId
    );
  }

  async getChatHistory(patientId?: string, dashboardId?: string) {
    const params = new URLSearchParams();
    if (patientId) params.set("patientId", patientId);
    if (dashboardId) params.set("dashboardId", dashboardId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const responseChat = await axios.get(
      `${this.baseUrl}/ai-chat/history${query}`,
      { validateStatus: status => status < 500 }
    );
    return responseChat.data as Response<HistoryChat>;
  }

  async ensureDashboard(patientId: string | undefined, dashboardId: string) {
    const response = await axios.put(`${this.baseUrl}/ai-chat/dashboard`, {
      ...(patientId ? { patientId } : {}),
      dashboardId
    });
    return response.data;
  }

  async createChatSession(patientId?: string) {
    const response = await axios.post(`${this.baseUrl}/ai-chat/sessions`, {
      ...(patientId ? { patientId } : {})
    });
    return response.data;
  }

  async getServiceSummary(
    input: InputChat,
    onChunk: (chunk: string) => void = () => undefined
  ) {
    const response: Response<ChatResponse> = {
      code: 0,
      message: ""
    };
    const userInput = input.message || "";
    if (userInput.trim().length === 0) {
      response.code = Http.BadRequest;
      response.message = "";
      return response;
    }
    return this.streamRequest(
      `${this.baseUrl}/ai-agent/summary`,
      input,
      onChunk
    );
  }

  async getDocumentSummary(
    input: InputChat,
    onChunk: (chunk: string) => void = () => undefined
  ) {
    const response: Response<ChatResponse> = {
      code: 0,
      message: ""
    };
    const userInput = input.message || "";
    if (userInput.trim().length === 0) {
      response.code = Http.BadRequest;
      response.message = "";
      return response;
    }
    return this.streamRequest(
      `${this.baseUrl}/ai-agent/summary`,
      input,
      onChunk
    );
  }
}
